import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
  type User as FirebaseUser,
  type Unsubscribe,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { COLLECTIONS } from '../constants/collections';
import type { User } from '../types/user';

const googleProvider = new GoogleAuthProvider();

// Apple sign-in is wired via Firebase's generic OAuthProvider. Production
// requires:
//   1. Apple Developer Program membership ($99/yr).
//   2. A Services ID with Sign in with Apple enabled and the Firebase OAuth
//      helper as the return URL (https://<project>.firebaseapp.com/__/auth/handler).
//   3. A .p8 signing key.
//   4. Services ID + Team ID + Key ID + private key entered in the Firebase
//      console (Authentication → Sign-in method → Apple).
// Until that's done, signInWithApple() will throw at the popup step. The
// button is still rendered so the auth UI matches the spec; clicking it
// surfaces a "configure Apple Sign-In" error that is easy to diagnose.
// TODO(launch): provision Apple Developer cert + Firebase Apple provider.
const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

function mapFirebaseUser(firebaseUser: FirebaseUser): Omit<User, 'subscription' | 'onboardingCompleted'> {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email!,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    createdAt: firebaseUser.metadata.creationTime
      ? new Date(firebaseUser.metadata.creationTime).getTime()
      : Date.now(),
    updatedAt: Date.now(),
  };
}

async function ensureUserDocument(firebaseUser: FirebaseUser): Promise<User> {
  const userRef = doc(db, COLLECTIONS.USERS, firebaseUser.uid);
  const snapshot = await getDoc(userRef);

  if (snapshot.exists()) {
    return snapshot.data() as User;
  }

  const newUser: User = {
    ...mapFirebaseUser(firebaseUser),
    subscription: 'free',
    onboardingCompleted: false,
  };

  await setDoc(userRef, {
    ...newUser,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return newUser;
}

export async function signUpWithEmail(email: string, password: string, displayName: string): Promise<User> {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(credential.user, { displayName });
  return ensureUserDocument(credential.user);
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return ensureUserDocument(credential.user);
}

export async function signInWithGoogle(): Promise<User> {
  const credential = await signInWithPopup(auth, googleProvider);
  return ensureUserDocument(credential.user);
}

export async function signInWithApple(): Promise<User> {
  const credential = await signInWithPopup(auth, appleProvider);
  return ensureUserDocument(credential.user);
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

export async function updateExamDate(
  userId: string,
  examDate: string | null,
  examLabel: string | null,
): Promise<void> {
  const { updateDoc } = await import('firebase/firestore');
  await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
    examDate,
    examLabel,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteAccount(): Promise<void> {
  const { getFunctions } = await import('firebase/functions');
  const { app } = await import('./firebase');
  const functions = getFunctions(app);
  const fn = httpsCallable<Record<string, never>, { ok: true }>(functions, 'deleteAccount');
  await fn({});
  // Cloud function deletes auth record; local auth state will flip to null via onAuthStateChanged.
  await firebaseSignOut(auth).catch(() => {
    // Auth may already be gone; ignore.
  });
}

/**
 * POPIA/GDPR data export: returns the full machine-readable archive of the
 * signed-in user's data (profile, content metadata, learning paths, tutor
 * transcripts, adaptation history, recalibration sessions, reports).
 */
export interface UserDataExport {
  meta: { format: string; userId: string; generatedAt: string };
  [key: string]: unknown;
}

export async function exportUserData(): Promise<UserDataExport> {
  const { getFunctions } = await import('firebase/functions');
  const { app } = await import('./firebase');
  const functions = getFunctions(app);
  const fn = httpsCallable<Record<string, never>, { ok: true; export: UserDataExport }>(
    functions, 'exportUserData',
  );
  const res = await fn({});
  return res.data.export;
}

/**
 * Claim any pending team invites matching the current user's email. Safe to
 * call on every auth-state change; no-ops when there are none.
 */
async function claimPendingTeamInvitesSafe(): Promise<void> {
  try {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    const functions = getFunctions(app);
    const fn = httpsCallable<Record<string, never>, { ok: true; claimed: unknown[] }>(
      functions, 'claimPendingTeamInvites',
    );
    await fn({});
  } catch {
    // Non-blocking: a failure here should never prevent auth from completing.
  }
}

export function onAuthChange(callback: (user: User | null) => void): Unsubscribe {
  let unsubscribeUserDoc: Unsubscribe | null = null;

  const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
    // Tear down any previous user-doc listener when the signed-in user changes
    // or signs out. Otherwise we'd leak listeners and emit stale data.
    if (unsubscribeUserDoc) {
      unsubscribeUserDoc();
      unsubscribeUserDoc = null;
    }

    if (!firebaseUser) {
      callback(null);
      return;
    }

    // Create the user doc on first sign-in so the snapshot listener has
    // something to read. Subsequent sessions just pass through.
    await ensureUserDocument(firebaseUser);
    void claimPendingTeamInvitesSafe();

    // Live-subscribe to users/{uid} so admin grants, webhook tier changes,
    // and profile edits flow into the auth store without needing a re-login.
    const userRef = doc(db, COLLECTIONS.USERS, firebaseUser.uid);
    unsubscribeUserDoc = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        callback(snap.data() as User);
      }
    });
  });

  return () => {
    if (unsubscribeUserDoc) unsubscribeUserDoc();
    unsubscribeAuth();
  };
}
